import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Alert,
  Badge,
  Tooltip,
  Grid,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stack,
  Fade,
  Skeleton,
  TableContainer,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

import { useClientService } from '../../services/clientService';
import type { Client, ReferralSummary } from '../../types';
import {
  translateGender,
  translateLengthOfService,
  translateDeliveryMethod,
  translateRiskType,
  translateEmployeeRelationship,
  translatePresentingIssue
} from '../../utils/catalogTranslations';

const ClientsPage: React.FC = () => {
  const theme = useTheme();
  const clientService = useClientService();
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout>();
  const [originFilter, setOriginFilter] = useState('ALL'); // ✅ NUEVO: Filtro por origen

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [referrals, setReferrals] = useState<ReferralSummary[]>([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🆕 Estado para dashboard stats desde backend
  const [dashboardStats, setDashboardStats] = useState({
    total: 0,
    sinReferrals: 0,
    terminados: 0,
    nuevosEsteMes: 0
  });

  const fetchClients = async (searchTerm?: string) => {
    setLoading(true);
    setError(null);
    try {
      // ✅ NUEVO: Enviar siempre el parámetro origin (incluso si es 'ALL')
      const res = await clientService.getClientsPaginated(page, rowsPerPage, searchTerm, originFilter);
      setClients(res.content);
      setTotalElements(res.totalElements);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al cargar los clientes';
      setError(`Error al cargar los clientes: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async (clientId: number) => {
    setReferralsLoading(true);
    try {
      const referralsData = await clientService.getClientReferrals(clientId);
      setReferrals(referralsData);
    } catch (error) {
      console.error('Error fetching referrals', error);
      setError('Error al cargar los referrals');
    } finally {
      setReferralsLoading(false);
    }
  };

  // 🆕 Función para obtener estadísticas del dashboard
  const fetchDashboardStats = async () => {
    try {
      const stats = await clientService.getClientDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Error fetching dashboard stats', error);
    }
  };

  useEffect(() => {
    fetchClients(search || undefined);
  }, [page, rowsPerPage, originFilter]); // ✅ NUEVO: Agregar originFilter a las dependencias

  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(0);
      fetchClients(search || undefined);
    }, 500);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [search]);

  // 🆕 useEffect para cargar estadísticas del dashboard
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const handleViewReferrals = async (client: Client) => {
    setSelectedClient(client);
    setModalOpen(true);
    if (client.hasReferrals) await fetchReferrals(client.id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedClient(null);
    setReferrals([]);
    setError(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SINCRONIZADO': return 'success';
      case 'PENDIENTE': return 'warning';
      case 'ERROR': return 'error';
      case 'SIN_CITAS': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('es-ES');

  const StatCard = ({ title, value, icon, color }: { title: string; value: string | number; icon: React.ReactNode; color: string }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${alpha(color, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color} sx={{ mt: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              {value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: color, width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 } }}>
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700, 
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Gestión de Clientes
        </Typography>
        <Typography variant="h6" color="text.secondary" fontWeight={400}>
          Administra y consulta la información de tus clientes
        </Typography>
      </Box>

      {/* 🔄 Stats Cards actualizadas con datos del backend */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Clientes
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAddIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.sinReferrals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sin Referrals
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.terminados}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Terminados
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.nuevosEsteMes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nuevos (Este mes)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros fijos */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="primary" />
            Búsqueda de Clientes
          </Typography>
        </Box>
        <TextField
          fullWidth
          placeholder="Buscar clientes por nombre, apellido..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'grey.50',
              '&:hover': {
                bgcolor: 'grey.100',
              },
              '&.Mui-focused': {
                bgcolor: 'background.paper',
              }
            }
          }}
        />
      </Paper>

      {error && (
        <Fade in={!!error}>
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Tabla con scroll como Accounts.tsx */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table size="small" stickyHeader aria-label="tabla de clientes">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Cuenta</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Client ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' } }}>Fecha de Alta</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', align: 'center' }}>Origen</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} /> Cargando clientes...
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box textAlign="center" py={{ xs: 3, sm: 4 }}>
                      <PeopleIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {search ? 'No se encontraron clientes' : 'No hay clientes registrados'}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {search 
                          ? 'Intenta con otros términos de búsqueda' 
                          : 'Los clientes aparecerán aquí cuando se registren'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client, index) => (
                  <TableRow 
                    key={client.id}
                    hover
                    sx={{ 
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {client.firstName} {client.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {client.accountName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={client.externalClientId || 'recibido'} 
                        size="small" 
                        variant="outlined"
                        color={client.externalClientId ? "default" : "success"}
                        sx={{ 
                          fontFamily: client.externalClientId ? 'monospace' : 'inherit',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {client.createdAt ? formatDate(client.createdAt) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {client.hasReferrals ? (
                        <Badge 
                          badgeContent={client.referralsCount} 
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              right: -3,
                              top: -3,
                            }
                          }}
                        >
                          <Chip
                            icon={<AssignmentIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />}
                            label={<Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Ver Referrals</Box>}
                            color="primary"
                            variant="outlined"
                            clickable
                            onClick={() => handleViewReferrals(client)}
                            size="small"
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease',
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          />
                        </Badge>
                      ) : (
                        <Chip 
                          label={<Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Sin Referrals</Box>}
                          color="default" 
                          variant="outlined" 
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={(client as any).origin === 'MANUAL' ? 'Manual' : (client as any).origin === 'PHONE_CALL' ? 'Llamada Telefónica' : (client as any).origin || 'N/A'}
                        color={(client as any).origin === 'MANUAL' ? 'primary' : (client as any).origin === 'PHONE_CALL' ? 'secondary' : 'default'}
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title="Ver detalles" arrow>
                        <IconButton
                          onClick={() => handleViewReferrals(client)}
                          size="small"
                          color="primary"
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ 
            borderTop: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            borderRadius: '0 0 8px 8px'
          }}
        />
      </Paper>

      {/* Enhanced Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal} 
        maxWidth="lg" 
        fullWidth 
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            position: 'relative'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {selectedClient?.firstName} {selectedClient?.lastName}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Cliente ID: {selectedClient?.id} | Cuenta: {selectedClient?.accountName}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseModal}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {selectedClient && (
            <Card sx={{ mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                  Información del Cliente
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        Género:
                      </Typography>
                      <Chip label={translateGender(selectedClient.gender)} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        Antigüedad:
                      </Typography>
                      <Typography variant="body2">
                        {translateLengthOfService(selectedClient.lengthOfService)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        Relación Laboral:
                      </Typography>
                      <Typography variant="body2">
                        {translateEmployeeRelationship(selectedClient.employeeRelationship)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        Creado por:
                      </Typography>
                      <Typography variant="body2">{selectedClient.createdBy || '-'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        Creado:
                      </Typography>
                      <Typography variant="body2">
                        {selectedClient.createdAt ? formatDate(selectedClient.createdAt) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        Actualizado:
                      </Typography>
                      <Typography variant="body2">
                        {selectedClient.updatedAt ? formatDate(selectedClient.updatedAt) : '-'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
            Referrals del Cliente
          </Typography>

          {referralsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={40} />
            </Box>
          ) : referrals.length > 0 ? (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Problema</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Método</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo de Riesgo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha llamada</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sesiones</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Citas</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referrals.map((referral) => (
                    <TableRow key={referral.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell>{referral.product}</TableCell>
                      <TableCell>{translatePresentingIssue(referral.presentingIssue)}</TableCell>
                      <TableCell>{translateDeliveryMethod(referral.deliveryMethod)}</TableCell>
                      <TableCell>{translateRiskType(referral.riskType as unknown as string)}</TableCell>
                      <TableCell>{formatDate(referral.callDate)}</TableCell>
                      <TableCell>
                        {referral.sessionsAuthorized ? (
                          <Typography variant="body2" fontFamily="monospace">
                            {referral.sessionsRemaining}/{referral.sessionsAuthorized}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{referral.claimsCount || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={referral.status} 
                          color={getStatusColor(referral.status)} 
                          size="small" 
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={6}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Sin referrals registrados
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Este cliente no tiene referrals asociados
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'grey.50' }}>
          <Button 
            onClick={handleCloseModal} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPage;